import React, { useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import type { Guest } from 'api/types';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface GuestViewModalProps {
  isOpen: boolean;
  guest: Guest | null;
  onClose: () => void;
  onEdit?: (guest: Guest) => void;
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (guestId: string) => void;
}

// ============================================================================
// READ-ONLY FIELD
// ============================================================================

const ReadOnlyField: React.FC<{ label: string; value?: string | number | boolean | null; span?: boolean }> = ({ label, value, span }) => (
  <div style={span ? { gridColumn: '1 / -1' } : undefined}>
    <label className="form-label">{label}</label>
    <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)' }}>
      {value != null && value !== '' ? (typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)) : '-'}
    </div>
  </div>
);

// ============================================================================
// CONSTANTS
// ============================================================================

const TITLE_LABELS: Record<string, string> = {
  Mr: 'Mr', Mrs: 'Mrs', Ms: 'Ms', Dr: 'Dr', Prof: 'Prof',
};

const ID_TYPE_LABELS: Record<string, string> = {
  Aadhar: 'Aadhar', Passport: 'Passport', DL: 'Driving License',
  'Voter ID': 'Voter ID', PAN: 'PAN', 'Govt ID': 'Government ID',
};

// ============================================================================
// COMPONENT
// ============================================================================

const GuestViewModal: React.FC<GuestViewModalProps> = ({
  isOpen,
  guest,
  onClose,
  onEdit,
  tabId,
  moduleName,
  onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !guest) return;
    minimize({
      id: `${tabId}-view-modal-${guest.id}`,
      title: `View - ${guest.firstName} ${guest.lastName}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView?.(guest.id),
    });
    onClose();
  }, [tabId, moduleName, guest, minimize, onClose, onRestoreView]);

  if (!isOpen || !guest) return null;

  const formatDate = (date?: string) => {
    if (!date) return '-';
    try { return new Date(date).toLocaleDateString(); } catch { return '-'; }
  };

  const fullName = `${guest.title ? guest.title + ' ' : ''}${guest.firstName} ${guest.lastName}`;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title">Guest Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* --- Basic Information --- */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
            <ReadOnlyField label="Title" value={guest.title ? (TITLE_LABELS[guest.title] || guest.title) : undefined} />
            <ReadOnlyField label="First Name" value={guest.firstName} />
            <ReadOnlyField label="Last Name" value={guest.lastName} />
          </div>

          {/* --- Status & VIP --- */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
            <div>
              <label className="form-label">Status</label>
              <div>
                <span className={`badge badge-${guest.isActive ? 'active' : 'inactive'}`}>
                  {guest.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            <div>
              <label className="form-label">VIP Guest</label>
              <div>
                <span className={`badge badge-${guest.isVip ? 'active' : 'inactive'}`}>
                  {guest.isVip ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
            <ReadOnlyField label="Nationality" value={guest.nationality} />
          </div>

          {/* --- Contact Information --- */}
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Contact Information</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
              <ReadOnlyField label="Phone" value={guest.phone} />
              <ReadOnlyField label="Email" value={guest.email} />
              <ReadOnlyField label="Date of Birth" value={formatDate(guest.dateOfBirth)} />
            </div>
          </div>

          {/* --- Address --- */}
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Address</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
              <ReadOnlyField label="Address" value={guest.address} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
              <ReadOnlyField label="City" value={guest.city} />
              <ReadOnlyField label="State" value={guest.state} />
              <ReadOnlyField label="Country" value={guest.country} />
              <ReadOnlyField label="Pincode" value={guest.pincode} />
            </div>
          </div>

          {/* --- Identity Document --- */}
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Identity Document</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              <ReadOnlyField label="ID Type" value={guest.idType ? (ID_TYPE_LABELS[guest.idType] || guest.idType) : undefined} />
              <ReadOnlyField label="ID Number" value={guest.idNumber} />
            </div>
          </div>

          {/* --- Notes --- */}
          {guest.notes && (
            <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
              <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Notes</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
                <ReadOnlyField label="Notes" value={guest.notes} />
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          {onEdit && (
            <button className="btn btn-primary" onClick={() => {
              onClose();
              onEdit(guest);
            }}>
              <IconSystem.edit size={16} />
              Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default GuestViewModal;
